import * as React from 'react';
import { ComponentProps, InputControlContext, LabelConfig } from 'ui';
import debounce from 'lodash/debounce';
import styles from './multi-emails.scss';

interface MultiEmailsProps extends ComponentProps {
  emails?: string[];
  placeholder?: string;
  onChange: (emails: string[]) => void;
  label?: LabelConfig;
  extraLabel?: string;
}

interface MultiEmailsState {
  propsEmails?: string[];
  emails: string[];
  inputValue?: string;
}

export class MultiEmails extends React.Component<MultiEmailsProps, MultiEmailsState> {  

  private emailInputRef: React.RefObject<HTMLInputElement | null>;
  private debounceOnChange: any;

  public static getDerivedStateFromProps(
    nextProps: MultiEmailsProps,
    prevState: MultiEmailsState
  ) {
    if (prevState.propsEmails !== nextProps.emails) {
      return {
        propsEmails: nextProps.emails || [],
        emails: nextProps.emails || [],
        inputValue: ''
      };
    }
    return null;
  }

  constructor(props: MultiEmailsProps) {
    super(props);
    this.state = {
      emails: [],
      inputValue: ''
    };

    this.debounceOnChange = debounce((emails: string[]) => {
      this.props.onChange?.(emails);
    }, 100);
    this.emailInputRef = React.createRef();
  }

  private isEmail = (value: string) => {
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}/ig;
    if(regex.test(value)) {
      return true;
    }
    return false;
  };

  private getLabel = (
    email: string,
    index: number,
    removeEmail: (index: number) => void
  ) => {
    const deleteEmail = () => removeEmail(index);
    
    return (
      !!email && <div data-tag key={index}>
        {email}
        <button type="button" data-tag-handle onClick={deleteEmail}>
          ×
        </button>
      </div>
    );
  };  

  private findEmailAddress = (value: string, isEnter?: boolean) => {    
    const validEmails: string[] = [];
    let inputValue: string = '';
    const regex = /[ ,;]/g;

    const addEmails = (email: string) => {
      const emails: string[] = this.state.emails;
      for (let i = 0; i < emails.length; i++) {
        if (emails[i] === email) {
          return false;
        }
      }
      validEmails.push(email);
      return true;
    };

    if(!value) {
      this.setState({
        inputValue: value
      });  
      return;
    }
    
    if (regex.test(value)) {
      const valueSplittedOnRegex = value.split(regex).filter(n => !!n);
      const emailInputAsString = valueSplittedOnRegex[0];
      this.isEmail(emailInputAsString) ? addEmails(emailInputAsString) : inputValue = inputValue;
    }   

    else if ( isEnter) {
      this.isEmail(value) ? addEmails(value) : inputValue = value;        
    } else {
      inputValue = value;
    }  

    this.setState({
      emails: [...this.state.emails, ...validEmails],
      inputValue: inputValue
    });
  
    if (validEmails.length) {
      this.debounceOnChange([...this.state.emails, ...validEmails]);
  
    }
  };

  private onChangeInputValue = (value: string) => {
    this.findEmailAddress(value);
  };

  private removeEmail = (index: number) => {

    this.setState(
      prevState => {
        return {
          emails: [
            ...prevState.emails.slice(0, index),
            ...prevState.emails.slice(index + 1)
          ]
        };
      },
      () => {
          this.debounceOnChange(this.state.emails);        
      }
    );
  };

  private handleOnKeyup = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        this.findEmailAddress(e.currentTarget.value, true);
        break;
      default:
    }
  };

  private handleOnKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {      
      case 'Enter':
        e.preventDefault();
        break;
      default:
    }
  };
  
  private handleOnChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    this.onChangeInputValue(e.currentTarget.value);

    private handleOnBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    this.findEmailAddress(e.currentTarget.value, true);
  };

  public render() {
    const { emails, inputValue } = this.state;
    const { componentId, label, info, infoLong, className, placeholder, extraLabel } = this.props;
    const inputExtraLabel = extraLabel && <div className={`control-label ${styles.extraLabel}`}>{extraLabel}</div>;

    return (
      <InputControlContext
        componentId={componentId}
        label={label}
        info={info}
        infoLong={infoLong}          
        className={className}>
        <div className="input-wrapper">
          <div className={`${styles.multiEmails}`} >
            {emails.map((email: string, index: number) =>
              this.getLabel(email, index, this.removeEmail)
            )}
            <input
              ref={this.emailInputRef}
              type="text"
              value={inputValue}
              onBlur={this.handleOnBlur}
              onChange={this.handleOnChange}
              placeholder={placeholder}
              onKeyUp={this.handleOnKeyup}
              onKeyDown={this.handleOnKeydown}
            />
          </div>
          {inputExtraLabel}
        </div>
      </InputControlContext>
    );
  }
}
